import { lazy, useEffect } from 'react';
import {
  createSearchParams,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Routes, SyncHook } from '../routeTypes';
import { DOCKET_PREFIX_PATH } from './DocketRoutes';
import { MEMBER_LIST_PREFIX_PATH } from './MemberListRoutes';

// Lazy load the ProtectedMemberPage component (which wraps MemberPage with access checking)
const ProtectedMemberPage = lazy(() =>
  import('react-components/member-page/ProtectedMemberPage').then(module => ({
    default: module.default,
  }))
);
import {
  selectMember,
  selectSelectedMemberId,
  selectSelectedTeamMemberId,
  selectTeamMember,
} from 'redux/slices/selectedMemberSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { HACK_setGlobalNavigationObjects } from './MemberPageRoutingHack';
import {
  selectMemberById,
  selectSelectedMember,
} from 'redux/slices/memberSlice.slice';

export const getMemberPagePath = (
  currentPathname: string,
  guideId: string,
  memberId: string
) => {
  let basePath = MEMBER_LIST_PREFIX_PATH;
  if (currentPathname.startsWith(DOCKET_PREFIX_PATH)) {
    basePath = DOCKET_PREFIX_PATH;
  }

  return `${basePath}/guide/${guideId || 'none'}/member/${memberId}`;
};

export const getMemberPageUrl = (
  currentPathname: string,
  guideId: string,
  memberId: string
) => {
  const path = getMemberPagePath(currentPathname, guideId, memberId);
  const host = window.location.host;
  const protocol = window.location.protocol;
  return `${protocol}//${host}${path}`;
};

const useSyncHook: SyncHook = () => {
  const dispatch = useAppDispatch();
  const { memberId: routeMemberId, guideId: routeGuideId } = useParams();
  const navigate = useNavigate();
  const reduxGuideId = useAppSelector(selectSelectedTeamMemberId);
  const reduxMemberId = useAppSelector(selectSelectedMemberId);
  const selectedMember = useAppSelector(selectSelectedMember);
  const [searchParams] = useSearchParams();

  const location = useLocation();

  // If the selected member updates in the route, make sure it is reflected in redux.
  useEffect(() => {
    dispatch(selectMember(routeMemberId));
  }, [routeMemberId]);

  // Update redux with the guieId in the route if it is actually the member's guide
  useEffect(() => {
    const reduxGuideDoesNotMatchRouteGuide =
      routeGuideId && routeGuideId !== reduxGuideId;

    if (reduxGuideDoesNotMatchRouteGuide) {
      dispatch(selectTeamMember(routeGuideId));
    }
  }, [routeGuideId, reduxGuideId, selectedMember]);

  // Update the route guideId if it isn't the member's actual guide
  useEffect(() => {
    const guideIdIsSynced = routeGuideId === reduxGuideId;
    const selectedMemberIsLoaded =
      selectedMember && selectedMember.id === reduxMemberId;
    const guideIsNotTheMembersGuide =
      routeGuideId !== selectedMember?.assignedGuideId;

    if (
      guideIdIsSynced &&
      selectedMemberIsLoaded &&
      guideIsNotTheMembersGuide
    ) {
      const url = getMemberPagePath(
        location.pathname,
        selectedMember?.assignedGuideId,
        selectedMember.id
      );

      navigate(
        {
          pathname: url,
          search: `${searchParams}`,
        },
        { replace: true }
      );
    }
  }, [routeGuideId, reduxGuideId, reduxMemberId, selectedMember]);

  // When leaving the member page we should clear the selected member
  useEffect(
    () => () => {
      dispatch(selectMember(null));
    },
    []
  );
};

const OldRouteReroute = () => {
  const { oldMemberId } = useParams();
  const location = useLocation();
  const member = useAppSelector(state =>
    selectMemberById(oldMemberId || '')(state)
  );

  // Try to use the member's actual guide ID if available, otherwise use 'none'
  // The sync hook will correct it to the proper guide ID once the member loads
  const guideId = member?.assignedGuideId || 'none';
  const newRoute = getMemberPagePath(
    location.pathname,
    guideId,
    oldMemberId || ''
  );

  return <Navigate to={newRoute} replace />;
};

const routes: Routes = [
  {
    path: '/members/:oldMemberId',
    element: <OldRouteReroute />,
  },
  {
    path: '/docket/guide/:guideId/member/:memberId',
    element: <ProtectedMemberPage />,
  },
  {
    path: '/member-list/guide/:guideId/member/:memberId',
    element: <ProtectedMemberPage />,
  },
];

const useNavigationHooks = () => {
  const navigate = useNavigate();
  const location = useLocation();

  HACK_setGlobalNavigationObjects({ navigate, location });

  return {
    toMemberPage: (
      guideId: string,
      memberId: string,
      feedItem?: {
        externalType?: string;
        externalId?: string | number;
      },
      needId?: string,
      replace?: boolean,
      showGoals?: boolean
    ) => {
      const {
        externalType: feedItemExternalType,
        externalId: feedItemExternalId,
      } = feedItem || {};

      const url = getMemberPagePath(location.pathname, guideId, memberId);

      const params = {
        ...(feedItemExternalType && { feedItemExternalType }),
        ...(feedItemExternalId && {
          feedItemExternalId: feedItemExternalId.toString(),
        }),
        ...(needId && { needId }),
        ...(showGoals && { showGoals: 'true' }),
      };
      // some navigation doesn't need to be included in the history i.e replace = true means we don't want the back button to take us there
      navigate(`${url}${params ? `?${createSearchParams(params)}` : ''}`, {
        replace: !!replace,
      });
    },
  };
};

export const memberPageRouteConfig = {
  routes,
  useSyncHook,
  useNavigationHooks,
};
